import { Background, BackgroundVariant, MarkerType, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { WikiBacklink, WikiLinkTarget } from "@/api";
import { Text } from "@/components/Text";
import { connectionsGraph } from "./connectionsGraph";
import { PageNodeView } from "./PageNodeView";

const nodeTypes = { page: PageNodeView };

/**
 * A page's connections as a small graph: what links here (left), what it links to (right).
 * Read-only: nodes open their page. Built from the page's own links and backlinks, so it only
 * shows pages the reader can see.
 */
export function WikiConnections({
  page,
  links,
  backlinks,
}: Readonly<{
  page: { spaceUuid: string; path: string; title: string };
  links: WikiLinkTarget[];
  backlinks: WikiBacklink[];
}>) {
  const { t } = useTranslation("knowledge", { keyPrefix: "connections" });
  const { nodes, edges } = useMemo(() => {
    const graph = connectionsGraph(page, links, backlinks);
    const titles = new Map(graph.nodes.map((n) => [n.id, n.data.title]));
    // Screen readers get "X links here" / "Links to Y" instead of React Flow's node ids.
    const describe = (source: string, target: string, label: unknown) => {
      if (source === graph.nodes[0].id) return t("linksTo", { title: titles.get(target) });
      if (typeof label === "string") return t("linksHereAs", { title: titles.get(source), kind: label });
      return t("linksHere", { title: titles.get(source) });
    };
    return {
      nodes: graph.nodes,
      edges: graph.edges.map((edge) => ({
        ...edge,
        ariaLabel: describe(edge.source, edge.target, edge.label),
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
        style: { stroke: "var(--color-border-strong)" },
        labelStyle: { fill: "var(--color-content-tertiary)", fontSize: 11 },
        labelBgStyle: { fill: "var(--color-surface-primary)" },
      })),
    };
  }, [page, links, backlinks, t]);

  if (nodes.length === 1) {
    return (
      <Text variant="body-sm" tone="tertiary">
        {t("empty")}
      </Text>
    );
  }
  return (
    <section aria-label={t("label")} className="flex flex-col gap-2">
      <Text variant="caption" tone="tertiary">
        {t("legend")}
      </Text>
      <div className="h-[360px] overflow-hidden rounded-lg border border-border-subtle bg-surface-base">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={false}
          preventScrolling={false}
          proOptions={{ hideAttribution: true }}
          colorMode="system"
        >
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </div>
    </section>
  );
}
